import {FC, useContext, useEffect, useMemo, useState} from 'react';
import {Box, Icon, SelectChangeEvent, Typography} from '@mui/material';
import {ReactComponent as WarningIcon} from '@/assets/icons/sidebar/warning-2.svg';
import {ReactComponent as ArrowRight} from '@/assets/icons/arrow-line-right.svg';
import Button from '@/shared/components/Button';
import Select from '@/shared/components/Select';
import {BODY1, BODY2, H6, SUBTITLE1} from '@/utils/constants';
import {IOrganizationContactResponse} from '@/utils/types';
import {UploadContactListContext} from '../../../UploadContactListContext';
import * as XLSX from 'xlsx';
import Radio from '@/shared/components/Radio';
import Modal from '@/shared/components/Modal';
import {OrganizationContactService} from '@/utils/services';
import {matchColumnStyle} from './MatchColumns.style';
import {useGetOrganizationId} from '@/utils/hooks';

const options = [
	{id: '1', name: 'First name', value: 'firstName'},
	{id: '2', name: 'Last name', value: 'lastName'},
	{id: '3', name: 'Email', value: 'email'},
	{id: '4', name: 'Phone', value: 'phoneNumber'},
	{id: '5', name: 'Meal', value: 'meals'},
	{id: '6', name: 'Address', value: 'address'},
	{id: '7', name: 'City', value: 'city'},
	{id: '8', name: 'State', value: 'state'},
	{id: '9', name: 'Zip', value: 'zip'},
];

export type SelectOptionsType = {name: string; id: string; value?: string};

const MatchColumns: FC = () => {
	const organizationId = useGetOrganizationId();

	const [step, setStep] = useState(0);
	const [isNotAllColumnsMatchModalOpen, setIsNotAllColumnsMatchModalOpen] = useState(false);
	const [sheetData, setSheetData] = useState<any[][]>([]);

	const {
		handleNextStep,
		selectedFile,
		sheetName,
		headerIndex,
		setExistingContactList,
		manageDuplicate,
		setManageDuplicate,
		setColumnForDuplicate,
	} = useContext(UploadContactListContext);

	const [excelHeaders, setExcelHeaders] = useState<string[]>([]);

	const [columnMapping, setColumnMapping] = useState<Record<string, string>>({});
	const [headerForChange, setHeaderForChange] = useState<Record<string, string>>({});

	const [updateDuplicationValue, setUpdateDuplicationValue] = useState('');
	const [skipDuplicationValue, setSkipDuplicationValue] = useState('');

	const unMatchedColumns = useMemo(() => {
		return excelHeaders.filter((header, index) => {
			const uniqueKey = `${index}-${header}`;

			return !headerForChange[uniqueKey];
		});
	}, [excelHeaders, headerForChange]);

	const getOptionsForColumn = (col: string, index: number) => {
		const currentUniqueKey = `${index}-${col}`;
		return options.filter(
			(option) =>
				!Object.values(headerForChange).includes(option.value!) || headerForChange[currentUniqueKey] === option.value
		);
	};

	useEffect(() => {
		if (selectedFile && sheetName) {
			const readFile = async () => {
				const data = await selectedFile.arrayBuffer();
				const workbook = XLSX.read(data, {type: 'array'});
				const worksheet = workbook.Sheets[sheetName];
				const json = XLSX.utils.sheet_to_json(worksheet, {header: 1});
				setSheetData(json as any[][]);
			};
			readFile();
		}
	}, [selectedFile, sheetName]);

	useEffect(() => {
		if (!selectedFile || !sheetName || headerIndex === undefined) return;

		const fetchHeaders = async () => {
			const data = await selectedFile.arrayBuffer();
			const workbook = XLSX.read(data, {type: 'array'});
			const worksheet = workbook.Sheets[sheetName];
			const rows = XLSX.utils.sheet_to_json(worksheet, {header: 1}) as any[][];

			const headers = rows[headerIndex!] || [];
			setExcelHeaders(headers);
		};

		fetchHeaders();
	}, [selectedFile, sheetName, headerIndex]);

	const updateRole = (excelCol: string, index: number) => (event: SelectChangeEvent, option: SelectOptionsType) => {
		const uniqueKey = `${index}-${excelCol}`;
		setColumnMapping((prev) => ({...prev, [uniqueKey]: option.id}));
		setHeaderForChange((prev) => ({...prev, [uniqueKey]: option.value!}));
	};

	const isDisableNextStep =
		(manageDuplicate === 'UpdateExisting' && !updateDuplicationValue) ||
		(manageDuplicate === 'Skip' && !skipDuplicationValue) ||
		manageDuplicate === '';

	const handleUpdateDuplicationValue = (event: SelectChangeEvent, option: SelectOptionsType) => {
		setUpdateDuplicationValue(option.id);
	};

	const handleSkipDuplicationValue = (event: SelectChangeEvent, option: SelectOptionsType) => {
		setSkipDuplicationValue(option.id);
	};

	const handleSkipMatch = () => {
		setStep(1);
		setIsNotAllColumnsMatchModalOpen(false);
	};

	const handleSubmitNextStep = async () => {
		if (!organizationId) return;

		const body = sheetData.slice(headerIndex! + 1) as any[][];
		const headers = sheetData[headerIndex!] as string[];

		const result = body.map((row) => {
			const rowObj: Record<string, any> = {};
			headers.forEach((originalHeader, index) => {
				const uniqueKey = `${index}-${originalHeader}`;
				const newHeader = headerForChange[uniqueKey];

				if (newHeader) {
					const cellValue = row[index];

					if (newHeader === 'meals') {
						rowObj[newHeader] = cellValue ? [String(cellValue)] : [];
					} else if (['email', 'firstName', 'lastName'].includes(newHeader)) {
						rowObj[newHeader] = cellValue ? String(cellValue) : '';
					} else {
						rowObj[newHeader] = cellValue ? String(cellValue) : null;
					}
				}
			});
			return rowObj;
		});

		const reqBody = {
			contacts: result as IOrganizationContactResponse[],
			organizationId,
			manageDuplicate,
			columnForDuplicate:
				manageDuplicate === 'CreateNew'
					? ''
					: manageDuplicate === 'UpdateExisting'
					? options.find((o) => o.id === updateDuplicationValue)?.value || ''
					: options.find((o) => o.id === skipDuplicationValue)?.value || '',
		};

		setColumnForDuplicate(reqBody.columnForDuplicate || '');

		try {
			const {data} = await OrganizationContactService.getExistingContactList(reqBody);
			const dataWithIds = data
				.filter((item) => item !== null)
				.map((item, index) => ({
					...item,
					_internalId: `${Date.now()}-${index}-${Math.random().toString(36).substr(2, 9)}`,
				}));

			setExistingContactList(dataWithIds as IOrganizationContactResponse[]);
			// setExistingContactList(data.filter((item) => item !== null) as IOrganizationContactResponse[]);
			handleNextStep();
		} catch (error) {
			console.error(error);
		}
	};

	return (
		<Box sx={{position: 'relative'}}>
			{step === 0 ? (
				<>
					<Box sx={matchColumnStyle.textWrapper}>
						<WarningIcon />
						<Typography>Unmatched columns will not be imported</Typography>
					</Box>
					<Box sx={matchColumnStyle.titleWrapper}>
						<Typography variant={SUBTITLE1} sx={matchColumnStyle.title}>
							Excel Columns:
						</Typography>
						<Typography variant={SUBTITLE1} sx={matchColumnStyle.title}>
							Will Become:
						</Typography>
					</Box>

					<Box sx={{display: 'flex', flexDirection: 'column', gap: '12px', paddingBottom: '100px'}}>
						{excelHeaders.map((col, idx) => (
							<Box sx={matchColumnStyle.titleWrapper} key={`${idx}-${col}`} display="flex" alignItems="center" mb={2}>
								<Typography variant={BODY1} sx={matchColumnStyle.columnTitle}>
									{col}
								</Typography>
								<ArrowRight />
								<Select
									sx={matchColumnStyle.select}
									options={getOptionsForColumn(col, idx)}
									placeholder="Choose column"
									value={columnMapping[`${idx}-${col}`] || ''}
									onChange={updateRole(col, idx)}
								/>
							</Box>
						))}
					</Box>

					<Box sx={matchColumnStyle.buttonWrapper}>
						<Button
							title="Confirm"
							onClick={
								excelHeaders.length === Object.keys(columnMapping).length
									? () => setStep(1)
									: () => setIsNotAllColumnsMatchModalOpen(true)
							}
						/>
					</Box>
				</>
			) : (
				<Box>
					<Box sx={matchColumnStyle.optionsWrapper}>
						<Typography variant={H6} sx={matchColumnStyle.title}>
							Let’s Manage Duplications:
						</Typography>

						{/* Update Existing */}
						<Box
							sx={{
								...matchColumnStyle.option,
								...(manageDuplicate === 'UpdateExisting' ? matchColumnStyle.active : {}),
							}}
						>
							<Typography sx={matchColumnStyle.recommended}>recommended</Typography>
							<Box sx={matchColumnStyle.optionTextWrapper}>
								<Radio
									onChange={(event) => setManageDuplicate(event.target.value)}
									value="UpdateExisting"
									checked={manageDuplicate === 'UpdateExisting'}
								/>
								<Box>
									<Typography variant={SUBTITLE1} sx={matchColumnStyle.mainText}>
										Update existing items
									</Typography>
									<Typography variant={BODY2} sx={matchColumnStyle.text}>
										Update items that exist in the table based on this columns:
									</Typography>
								</Box>
							</Box>
							<Box sx={matchColumnStyle.selectItem}>
								<Select
									placeholder="Choose column"
									options={options}
									value={updateDuplicationValue}
									onChange={handleUpdateDuplicationValue}
								/>
							</Box>
						</Box>

						{/* Skip Items */}
						<Box sx={{...matchColumnStyle.option, ...(manageDuplicate === 'Skip' ? matchColumnStyle.active : {})}}>
							<Box sx={matchColumnStyle.optionTextWrapper}>
								<Radio
									onChange={(event) => setManageDuplicate(event.target.value)}
									value="Skip"
									checked={manageDuplicate === 'Skip'}
								/>
								<Box>
									<Typography variant={SUBTITLE1} sx={matchColumnStyle.mainText}>
										Skip items
									</Typography>
									<Typography variant={BODY2} sx={matchColumnStyle.text}>
										Skip items that exist in the board based on this column:
									</Typography>
								</Box>
							</Box>
							<Box sx={matchColumnStyle.selectItem}>
								<Select
									placeholder="Choose column"
									options={options}
									value={skipDuplicationValue}
									onChange={handleSkipDuplicationValue}
								/>
							</Box>
						</Box>

						{/* Create New */}
						<Box sx={{...matchColumnStyle.option, ...(manageDuplicate === 'CreateNew' ? matchColumnStyle.active : {})}}>
							<Box sx={matchColumnStyle.optionTextWrapper}>
								<Radio
									onChange={(event) => setManageDuplicate(event.target.value)}
									value="CreateNew"
									checked={manageDuplicate === 'CreateNew'}
								/>
								<Box>
									<Typography variant={SUBTITLE1} sx={matchColumnStyle.mainText}>
										Create new
									</Typography>
									<Typography variant={BODY2} sx={matchColumnStyle.text}>
										Create new items from the imported data in my Excel
									</Typography>
								</Box>
							</Box>
						</Box>
					</Box>

					<Box sx={matchColumnStyle.buttonWrapper}>
						<Button title="Next Step" onClick={handleSubmitNextStep} disabled={isDisableNextStep} />
					</Box>
				</Box>
			)}

			<Modal
				open={isNotAllColumnsMatchModalOpen}
				onClose={() => setIsNotAllColumnsMatchModalOpen(false)}
				title="Not All Columns Matched"
				description="Do you want to continue?"
				mainIcon={
					<Icon sx={matchColumnStyle.warningIcon}>
						<WarningIcon />
					</Icon>
				}
				mainButtonTitle="Continue"
				onSave={handleSkipMatch}
				closeButtonTitle="Cancel"
				color="warning"
				sx={matchColumnStyle.modal}
				isShowSaveButton
				isShowCloseButton
			>
				<Typography sx={{color: 'var(--warning-600)'}}>Columns not matched: {unMatchedColumns.join(', ')}</Typography>
			</Modal>
		</Box>
	);
};

export default MatchColumns;
